import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Breadcrumb, ButtonWithIcon, Icon, Modal } from '@/components/atoms'
import { FileUploadWithModal, PageContainer } from '@/components/organisms'
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper'
import { useCreateBulkPointModifiers } from '@/pages/BulkPointModifiers/CreateBulkPointModifiers-hook'
import { PageHeader, SaveCancelFooter, SectionTitle } from '@/components/molecules'
import { ROUTES } from '@/utils/constants/routesConstants'
import { MODAL_TYPE, SIZE } from '@/utils/constants/globalStyle'
import { COLOR } from '@/utils/constants/colors'

import './_createBulkPointModifiers.scss'
import { useAppDispatch } from '@/store'
import { setBulkModifiers } from '@/store/slices/bulkPointSlice'
import { ACTION_DROPDOWN, Constants, STORE_SELECTOR_DROPDOWN } from '@/utils/constants/commonConstants'
import { convertToLowerCase } from '@/utils/commons'
import { setStoreListUrl } from '@/store/slices/salesTargetSlice'
import useReviewBulkPointModifier from '@/containers/ReviewBulkPointModifiers/ReviewBulkPointModifier-hooks'

export const CreateBulkPointModifiers = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isCancel, setIsCancel] = useState(false);

  const [openUpload, setOpenUpload] = useState(false)
  const {
    formData,
    setFormData,
    handleFormChange,
    handleFileDownload,
    validateFormData,
    getScheduleInfo,
    bulkModifierSetupJsonSchema,
    bulkModifierSetupUISchema,
    rulesJsonSchema,
    rulesUISchema,
    numOfDaysSchema,
    numOfDaysUISchema,
    numOfPointsSchema,
    numOfPointsUISchema,
    scheduleJsonSchema,
    scheduleUISchema,
    startDateJsonSchema
  } = useCreateBulkPointModifiers()
  const { handleDraft } = useReviewBulkPointModifier()
  const handleSaveDraft = () => {
    handleDraft()
  }
  useEffect(() => {
    dispatch(setBulkModifiers(formData));
  }, [dispatch, formData]);

  const handleNext = () => {
    dispatch(setBulkModifiers(formData))
    navigate(ROUTES.BULK_POINT_MODIFIER_REVIEW)
  }

  const handleCancel = () => {
    setIsCancel(!isCancel);
  }
  const onClickPrimaryButton = () => {
    navigate(ROUTES.BULK_POINT_MODIFIERS)
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }
  const isInValidData = validateFormData({ ...formData })
  const { scheduleRowClass, showStartDate } = getScheduleInfo(formData?.schedule?.toLowerCase())

  /* Rendering startDate ui schema for dynamic validation */
  const renderStartDate = (startDate, endDate) => ({
    type: 'HorizontalLayout',
    className: '',
    elements: [
      {
        type: 'HorizontalLayout',
        elements: [
          {
            type: 'Control',
            scope: '#/properties/startDate',
            component: 'datepicker',
            label: 'Start Date*',
            placeholder: '',
            isDark: true,
            required: false,
            dependentDate: endDate,
            validationRules: [
              {
                type: 'required',
                msg: ' is required',
              },
              {
                type: 'startDate',
                msg: ' cannot be in the past.',
              },
              {
                type: 'startDate',
                endDate: endDate,
              },
            ],
          },
        ],
      },
    ],
  })

  return (
    <div className="flex flex-col">
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: '',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },
            {
              href: '',
              label: 'Point Modifiers',
            },
            {
              href: '',
              label: 'Create Bulk Modifiers',
            },
          ],
        }}
        id={'cbpm_bradcrumb'}
      />
      <PageHeader title="Create Bulk Point Modifiers" />
      <PageContainer data-testid="create-bulk-point-modifiers-content">
        <div className="createBulkPointModifiersContent">
          <div className="w-1/2">
            <SectionTitle label="Bulk Modifier Setup" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <JsonFormsWrapper
                jsonschema={bulkModifierSetupJsonSchema}
                uischema={bulkModifierSetupUISchema}
                data={formData}
                onChange={data => handleFormChange(data)}
              />
            </div>
          </div>

          <div className="w-1/2">
            <SectionTitle label="Rules" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <JsonFormsWrapper
                jsonschema={rulesJsonSchema}
                uischema={rulesUISchema}
                data={formData}
                onChange={data => {
                  data.numberOfDays = ''
                  data.numberOfPoints = ''
                  handleFormChange(data)
                }}
              />
              {convertToLowerCase(formData?.actions) === ACTION_DROPDOWN.REMOVE_ALL_EXCEPT && (
                <JsonFormsWrapper
                  jsonschema={numOfDaysSchema}
                  uischema={numOfDaysUISchema}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
              {convertToLowerCase(formData?.actions) === ACTION_DROPDOWN.REMOVE_NUM_POINTS && (
                <JsonFormsWrapper
                  jsonschema={numOfPointsSchema}
                  uischema={numOfPointsUISchema}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
              {convertToLowerCase(formData?.actions) === ACTION_DROPDOWN.ADD_POINTS && (
                <JsonFormsWrapper
                  jsonschema={numOfPointsSchema}
                  uischema={numOfPointsUISchema}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
            </div>
          </div>

          <div className={scheduleRowClass}>
            <SectionTitle label="Availability" />
            <div className="flex flex-col gap-4 lg:flex-row">
              <JsonFormsWrapper
                jsonschema={scheduleJsonSchema}
                uischema={scheduleUISchema}
                data={formData}
                onChange={data => {
                  data.startDate = ''
                  data.endDate = ''
                  handleFormChange(data)
                }}
              />
              {showStartDate && (
                <JsonFormsWrapper
                  jsonschema={startDateJsonSchema}
                  uischema={renderStartDate(formData?.startDate, formData?.endDate)}
                  data={formData}
                  onChange={data => handleFormChange(data)}
                />
              )}
            </div>
          </div>

          {/* Excel Upload' */}

          {convertToLowerCase(formData?.storeSelector) === STORE_SELECTOR_DROPDOWN.EXCEL_UPLOAD && (
            <div className="">
              <SectionTitle label="Upload File*" />
              <div className="flex flex-col gap-2 lg:flex-row">
                <span className="downLoadIconContainer" onClick={handleFileDownload}>
                  <Icon icon="file_download" size={SIZE.SMALL} isDark={true} />
                </span>
                {formData?.targetMediaFile?.length == 1 ? (
                  <div className="main_upload-title ml-2  flex items-center rounded-lg px-2 py-3.5 text-xs">
                    <Icon alt="file" className="insert_file_icon" icon="insert_drive_file" size="small" />
                    <p className="w-[105px] truncate pl-2">{formData?.targetMediaFile?.[0]}</p>
                    <Icon
                      alt="cached"
                      className="cach_ed_icon"
                      icon="cached"
                      size="small"
                      data-testid="file-delete-icon"
                      onClick={() => {
                        setFormData(prev => ({...prev, targetMediaFile: []}))
                        dispatch(setStoreListUrl(''))
                        setOpenUpload(true)
                      }}
                    />
                  </div>
                ) : (
                  <ButtonWithIcon
                    uiSchema={{
                      placeholder: 'Upload Target Template',
                      title: 'Upload Target Template',
                      size: SIZE.SMALL,
                      variant: COLOR.PRIMARY,
                    }}
                    iconLeading="upload"
                    handleButton={() => setOpenUpload(true)}
                  />
                )}
              </div>
              <FileUploadWithModal
                title="Upload File"
                fileType="csv"
                uploadFor={Constants.BulkModifiers}
                openUpload={openUpload}
                setOpenUpload={setOpenUpload}
                setFormData={setFormData}
                formData={formData}
                customErrorMessages={{
                  wrongFileType: 'Wrong file type, please use CSV file',
                  wrongFormat: 'The format being used is not correct, please use correct file format',
                }}
              />
            </div>
          )}
        </div>
      </PageContainer>
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleSaveDraft}
        FormError={isInValidData}
      />

      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title="Are you sure you want to cancel?"
          text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      ) : null}
    </div>
  )
}
